// src/utils/blobHelpers.js
// ✅ Blob/UUID 관련 공통 유틸리티
// - DB 레이어와 Store 레이어 모두에서 사용 가능
// - 브라우저 API 래핑

/* ═══════════════════════════════════════════════════════════
 * UUID Generation
 * ═══════════════════════════════════════════════════════════ */

/**
 * UUID v4 생성
 * - crypto.randomUUID() 지원 시 사용 (더 안전)
 * - 미지원 시 폴백 구현
 * @returns {string}
 */
export function generateUUID() {
  // 최신 브라우저
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  // 폴백: 정규식 기반
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/* ═══════════════════════════════════════════════════════════
 * Blob URL Management
 * ═══════════════════════════════════════════════════════════ */

// Blob URL 추적 (메모리 누수 방지)
const blobUrlRegistry = new Map(); // id → Set<blobUrl>

/**
 * Blob URL 생성
 * @param {Blob|File} blob
 * @param {string|number} [trackingId] - 추적용 ID (파일 ID 등)
 * @returns {string} blob URL
 */
export function createBlobUrl(blob, trackingId) {
  const url = URL.createObjectURL(blob);

  if (trackingId != null) {
    if (!blobUrlRegistry.has(trackingId)) {
      blobUrlRegistry.set(trackingId, new Set());
    }
    blobUrlRegistry.get(trackingId).add(url);
  }

  return url;
}

/**
 * Blob URL 해제
 * @param {string} url
 * @param {string|number} [trackingId]
 */
export function revokeBlobUrl(url, trackingId) {
  URL.revokeObjectURL(url);

  if (trackingId != null && blobUrlRegistry.has(trackingId)) {
    blobUrlRegistry.get(trackingId).delete(url);
  }
}

/**
 * 특정 ID에 연결된 모든 Blob URL 해제
 * @param {string|number} trackingId
 */
export function revokeAllBlobUrls(trackingId) {
  const urls = blobUrlRegistry.get(trackingId);
  
  if (urls) {
    for (const url of urls) {
      URL.revokeObjectURL(url);
    }
    blobUrlRegistry.delete(trackingId);
  }
}

/**
 * 모든 Blob URL 해제 (앱 종료 시 등)
 */
export function revokeAllTrackedBlobUrls() {
  for (const urls of blobUrlRegistry.values()) {
    for (const url of urls) {
      URL.revokeObjectURL(url);
    }
  }
  blobUrlRegistry.clear();
}

/* ═══════════════════════════════════════════════════════════
 * File Conversion Helpers
 * ═══════════════════════════════════════════════════════════ */

/**
 * IndexedDB 레코드 → File 객체 변환
 * @param {Object} record - { name, type, content, lastModified }
 * @returns {{ file: File, blobUrl: string }}
 */
export function recordToFile(record) {
  if (!record || !record.content) {
    return { file: null, blobUrl: null };
  }

  const blob = new Blob([record.content], { type: record.type });
  const file = new File([blob], record.name, {
    type: record.type,
    lastModified: record.lastModified,
  });

  const blobUrl = createBlobUrl(file, record.id);

  return { file, blobUrl };
}

/**
 * File 객체 → ArrayBuffer (IndexedDB 저장용)
 * @param {File} file
 * @returns {Promise<ArrayBuffer>}
 */
export async function fileToArrayBuffer(file) {
  return file.arrayBuffer();
}

/**
 * Base64 → Blob 변환
 * @param {string} base64
 * @param {string} mimeType
 * @returns {Blob}
 */
export function base64ToBlob(base64, mimeType = 'application/octet-stream') {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mimeType });
}

/**
 * Blob → Base64 변환
 * @param {Blob} blob
 * @returns {Promise<string>}
 */
export function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/* ═══════════════════════════════════════════════════════════
 * Default Export
 * ═══════════════════════════════════════════════════════════ */
export default {
  generateUUID,
  createBlobUrl,
  revokeBlobUrl,
  revokeAllBlobUrls,
  revokeAllTrackedBlobUrls,
  recordToFile,
  fileToArrayBuffer,
  base64ToBlob,
  blobToBase64,
};